import { useState, useEffect, useMemo } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToEmpleados,
  subscribeToCasas,
  subscribeToServicios,
  subscribeToPagosNomina,
  subscribeToQuotations,
  empleadoEnServicio,
  Empleado,
  Casa,
  Servicio,
  PagoNomina,
  Quotation,
} from "../../firebase/firebaseService";
import {
  FaUsers,
  FaHome,
  FaMoneyBillWave,
  FaChartBar,
  FaClock,
  FaArrowUp,
  FaArrowDown,
  FaExclamationTriangle,
  FaCheckCircle,
  FaFileAlt,
  FaTrophy,
  FaStar,
  FaCalendarAlt,
  FaHandHoldingUsd,
} from "react-icons/fa";

const MESES = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

const getQuincenaKey = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const q = date.getDate() <= 15 ? "1" : "2";
  return `${y}-${m}-${q}`;
};

const getPastQuincenas = (n: number): string[] => {
  const list: string[] = [];
  let y = new Date().getFullYear();
  let mo = new Date().getMonth() + 1;
  let q = new Date().getDate() <= 15 ? 1 : 2;
  for (let i = 0; i < n; i++) {
    if (q === 1) { q = 2; mo--; if (mo === 0) { mo = 12; y--; } }
    else q = 1;
    list.push(`${y}-${String(mo).padStart(2,"0")}-${q}`);
  }
  return list;
};

const getQuincenaEndDate = (key: string): Date => {
  const [y, m, q] = key.split("-").map(Number);
  if (q === 1) return new Date(y, m - 1, 15, 23, 59, 59);
  return new Date(y, m, 0, 23, 59, 59);
};

// Mini bar component
const MiniBar = ({ value, max, color }: { value: number; max: number; color: string }) => (
  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
    <div
      className={`h-full rounded-full transition-all duration-700 ${color}`}
      style={{ width: max > 0 ? `${Math.min(100, (value / max) * 100)}%` : "0%" }}
    />
  </div>
);

// Stat card
const StatCard = ({
  icon: Icon,
  label,
  value,
  sub,
  gradient,
  iconBg,
}: {
  icon: any;
  label: string;
  value: string;
  sub?: string;
  gradient: string;
  iconBg: string;
}) => (
  <div className={`relative overflow-hidden rounded-2xl p-6 ${gradient} text-white shadow-lg`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs font-semibold opacity-80 uppercase tracking-wider">{label}</p>
        <p className="text-3xl font-extrabold mt-2">{value}</p>
        {sub && <p className="text-xs opacity-70 mt-1">{sub}</p>}
      </div>
      <div className={`w-12 h-12 rounded-2xl ${iconBg} flex items-center justify-center`}>
        <Icon size={22} className="text-white" />
      </div>
    </div>
    {/* Decorative circle */}
    <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-white/10" />
    <div className="absolute -bottom-8 -right-8 w-32 h-32 rounded-full bg-white/5" />
  </div>
);

export default function ResumenAdmin() {
  const [empleados, setEmpleados] = useState<Empleado[]>([]);
  const [casas, setCasas] = useState<Casa[]>([]);
  const [servicios, setServicios] = useState<Servicio[]>([]);
  const [pagosNomina, setPagosNomina] = useState<PagoNomina[]>([]);
  const [cotizaciones, setCotizaciones] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let count = 0;
    const check = () => { count++; if (count >= 5) setLoading(false); };
    const u1 = subscribeToEmpleados((d) => { setEmpleados(d); check(); });
    const u2 = subscribeToCasas((d) => { setCasas(d); check(); });
    const u3 = subscribeToServicios((d) => { setServicios(d); check(); });
    const u4 = subscribeToPagosNomina((d) => { setPagosNomina(d); check(); });
    const u5 = subscribeToQuotations((d) => { setCotizaciones(d); check(); });
    return () => { u1(); u2(); u3(); u4(); u5(); };
  }, []);

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;

  // ── Core metrics ──────────────────────────────────────────────────────────
  const mesServicios = useMemo(
    () => servicios.filter(s => s.fechaServicio.getFullYear() === currentYear && s.fechaServicio.getMonth() === currentMonth),
    [servicios, currentMonth, currentYear]
  );
  const prevMesServicios = useMemo(
    () => servicios.filter(s => s.fechaServicio.getFullYear() === prevYear && s.fechaServicio.getMonth() === prevMonth),
    [servicios, prevMonth, prevYear]
  );

  const gananciasMes = mesServicios.reduce((s, sv) => s + (sv.precioServicio || 0), 0);
  const gananciasPrevMes = prevMesServicios.reduce((s, sv) => s + (sv.precioServicio || 0), 0);
  const gananciaDiff = gananciasPrevMes > 0 ? ((gananciasMes - gananciasPrevMes) / gananciasPrevMes) * 100 : 0;

  const horasMes = mesServicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);
  const horasPrevMes = prevMesServicios.reduce((s, sv) => s + sv.horasTrabajadas, 0);

  // Deuda: servicios without a nomina payment
  const currentQ = getQuincenaKey(now);
  const allQ = [currentQ, ...getPastQuincenas(5)];
  const deudaTotal = useMemo(() => {
    return empleados.reduce((total, emp) => {
      const deuda = allQ.reduce((sum, q) => {
        const svs = servicios.filter(s => s.quincena === q && (empleadoEnServicio(s, emp.id!)));
        const paid = pagosNomina.some(p => p.empleadoId === emp.id && p.quincena === q);
        if (paid || svs.length === 0) return sum;
        return sum + svs.reduce((s, sv) => s + sv.horasTrabajadas, 0) * (emp.costoPorHora || 0);
      }, 0);
      return total + deuda;
    }, 0);
  }, [empleados, servicios, pagosNomina, allQ]);

  // ── Top houses ────────────────────────────────────────────────────────────
  const topCasas = useMemo(() => {
    const map: Record<string, { casa: Casa; count: number; revenue: number }> = {};
    servicios.forEach((sv) => {
      const casa = casas.find(c => c.id === sv.casaId);
      if (!casa) return;
      if (!map[sv.casaId]) map[sv.casaId] = { casa, count: 0, revenue: 0 };
      map[sv.casaId].count++;
      map[sv.casaId].revenue += sv.precioServicio || 0;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 6);
  }, [servicios, casas]);

  const maxCasaRevenue = topCasas[0]?.revenue || 1;

  // ── Top employees ─────────────────────────────────────────────────────────
  const topEmpleados = useMemo(() => {
    const map: Record<string, { emp: Empleado; count: number; horas: number }> = {};
    servicios.forEach((sv) => {
      const emp = empleados.find(e => e.id === sv.empleadoPrincipalId);
      if (!emp) return;
      if (!map[sv.empleadoPrincipalId]) map[sv.empleadoPrincipalId] = { emp, count: 0, horas: 0 };
      map[sv.empleadoPrincipalId].count++;
      map[sv.empleadoPrincipalId].horas += sv.horasTrabajadas;
    });
    return Object.values(map).sort((a, b) => b.count - a.count).slice(0, 6);
  }, [servicios, empleados]);

  const maxEmpCount = topEmpleados[0]?.count || 1;

  // ── Monthly breakdown (last 6 months) ────────────────────────────────────
  const mesesData = useMemo(() => {
    const months: { label: string; count: number; revenue: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      let mo = currentMonth - i;
      let yr = currentYear;
      if (mo < 0) { mo += 12; yr--; }
      const svs = servicios.filter(s => s.fechaServicio.getMonth() === mo && s.fechaServicio.getFullYear() === yr);
      months.push({
        label: MESES[mo].slice(0, 3),
        count: svs.length,
        revenue: svs.reduce((s, sv) => s + (sv.precioServicio || 0), 0),
      });
    }
    return months;
  }, [servicios, currentMonth, currentYear]);

  const maxMonthRevenue = Math.max(...mesesData.map(m => m.revenue), 1);

  // ── Nomina status ─────────────────────────────────────────────────────────
  const pagosUrgentes = useMemo(() => {
    return empleados.filter(emp => {
      const svs = servicios.filter(s => s.quincena === currentQ && (empleadoEnServicio(s, emp.id!)));
      const paid = pagosNomina.some(p => p.empleadoId === emp.id && p.quincena === currentQ);
      const ended = getQuincenaEndDate(currentQ) < now;
      return svs.length > 0 && !paid && ended;
    });
  }, [empleados, servicios, pagosNomina, currentQ]);

  const cotizacionesPendientes = cotizaciones.filter(c => c.status === "pending").length;

  // ── Pagos pendientes por casas (total general) ─────────────────────────────
  const pagosPendientesTotales = useMemo(() => {
    const pendientes = servicios.filter(s => (s as any).estadoPago !== "pagado");
    return pendientes.reduce((sum, s) => sum + (s.precioServicio || 0), 0);
  }, [servicios]);

  const pagosPendientesCount = useMemo(() => {
    return servicios.filter(s => (s as any).estadoPago !== "pagado").length;
  }, [servicios]);

  if (loading) {
    return (
      <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
        <SiderBar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-12 h-12 border-4 border-purple-300 border-t-purple-600 rounded-full mx-auto mb-4" />
            <p className="text-gray-500 font-medium">Cargando estadísticas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />

      <div className="flex-1 overflow-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">Resumen General</h1>
              <p className="text-purple-100 text-sm mt-1">
                {MESES[currentMonth]} {currentYear} &nbsp;·&nbsp; Gobies Cleaning Services
              </p>
            </div>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur px-4 py-2 rounded-xl">
              <FaCalendarAlt className="text-white" size={14} />
              <span className="text-white text-sm font-semibold">
                Actualizado en tiempo real
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 lg:p-8 space-y-8">

          {/* ── Hero stats ──────────────────────────────────────────────── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <StatCard
              icon={FaUsers}
              label="Empleados Activos"
              value={String(empleados.filter(e => e.activo).length)}
              sub={`${empleados.length} total registrados`}
              gradient="bg-gradient-to-br from-violet-600 to-indigo-700"
              iconBg="bg-white/20"
            />
            <StatCard
              icon={FaHome}
              label="Casas Activas"
              value={String(casas.filter(c => c.activa).length)}
              sub={`${casas.length} total registradas`}
              gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
              iconBg="bg-white/20"
            />
            <StatCard
              icon={FaMoneyBillWave}
              label={`Ingresos ${MESES[currentMonth].slice(0,3)}`}
              value={`$${gananciasMes.toLocaleString("es-ES", { maximumFractionDigits: 0 })}`}
              sub={
                gananciaDiff >= 0
                  ? `↑ ${gananciaDiff.toFixed(1)}% vs mes anterior`
                  : `↓ ${Math.abs(gananciaDiff).toFixed(1)}% vs mes anterior`
              }
              gradient="bg-gradient-to-br from-blue-500 to-cyan-600"
              iconBg="bg-white/20"
            />
            <StatCard
              icon={FaHandHoldingUsd}
              label="Pagos Pendientes"
              value={`$${pagosPendientesTotales.toLocaleString("es-ES", { maximumFractionDigits: 0 })}`}
              sub={`${pagosPendientesCount} servicio(s) sin cobrar`}
              gradient="bg-gradient-to-br from-rose-500 to-pink-600"
              iconBg="bg-white/20"
            />
            <StatCard
              icon={FaExclamationTriangle}
              label="Deuda Total Empleados"
              value={`$${deudaTotal.toLocaleString("es-ES", { maximumFractionDigits: 0 })}`}
              sub={`${pagosUrgentes.length} pago(s) urgente(s)`}
              gradient="bg-gradient-to-br from-amber-500 to-orange-600"
              iconBg="bg-white/20"
            />
          </div>

          {/* ── Secondary stats ──────────────────────────────────────────── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: `Servicios ${MESES[currentMonth].slice(0,3)}`, value: mesServicios.length, icon: FaCalendarAlt, color: "text-blue-600", bg: "bg-blue-50" },
              { label: "Horas este mes", value: `${horasMes.toFixed(1)}h`, icon: FaClock, color: "text-violet-600", bg: "bg-violet-50" },
              { label: "Cotizaciones pendientes", value: cotizacionesPendientes, icon: FaFileAlt, color: "text-amber-600", bg: "bg-amber-50" },
              { label: "Promedio horas/servicio", value: mesServicios.length > 0 ? `${(horasMes / mesServicios.length).toFixed(1)}h` : "0h", icon: FaChartBar, color: "text-emerald-600", bg: "bg-emerald-50" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
                <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center mb-3`}>
                  <s.icon size={18} className={s.color} />
                </div>
                <p className="text-2xl font-bold text-gray-800">{s.value}</p>
                <p className="text-[13px] text-gray-400 mt-1 font-medium">{s.label}</p>
              </div>
            ))}
          </div>

          {/* ── Monthly chart + Quincena comparison ──────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Revenue Bar Chart */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-bold text-gray-800 text-base">Ingresos por Mes</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Últimos 6 meses</p>
                </div>
                <div className="w-8 h-8 rounded-xl bg-blue-50 flex items-center justify-center">
                  <FaChartBar size={16} className="text-blue-500" />
                </div>
              </div>
              <div className="flex items-end gap-2 h-40">
                {mesesData.map((m, i) => {
                  const pct = maxMonthRevenue > 0 ? (m.revenue / maxMonthRevenue) * 100 : 0;
                  const isLast = i === mesesData.length - 1;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-500 font-medium">${m.revenue > 0 ? (m.revenue/1000).toFixed(1)+"k" : "0"}</span>
                      <div className="w-full relative" style={{ height: "96px" }}>
                        <div
                          className={`absolute bottom-0 w-full rounded-t-lg transition-all duration-700 ${isLast ? "bg-gradient-to-t from-blue-600 to-cyan-400" : "bg-gradient-to-t from-blue-200 to-blue-100"}`}
                          style={{ height: `${Math.max(4, pct)}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-semibold ${isLast ? "text-blue-600" : "text-gray-400"}`}>{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Quincena comparison */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="font-bold text-gray-800 text-base">Servicios por Mes</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Volumen de trabajo</p>
                </div>
                <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
                  <FaCalendarAlt size={15} className="text-violet-500" />
                </div>
              </div>
              <div className="flex items-end gap-2 h-40">
                {mesesData.map((m, i) => {
                  const maxC = Math.max(...mesesData.map(x => x.count), 1);
                  const pct = (m.count / maxC) * 100;
                  const isLast = i === mesesData.length - 1;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[10px] text-gray-500 font-medium">{m.count}</span>
                      <div className="w-full relative" style={{ height: "96px" }}>
                        <div
                          className={`absolute bottom-0 w-full rounded-t-lg transition-all duration-700 ${isLast ? "bg-gradient-to-t from-violet-600 to-purple-400" : "bg-gradient-to-t from-violet-200 to-violet-100"}`}
                          style={{ height: `${Math.max(4, pct)}%` }}
                        />
                      </div>
                      <span className={`text-[10px] font-semibold ${isLast ? "text-violet-600" : "text-gray-400"}`}>{m.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Top casas + Top empleados ─────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Casas */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-bold text-gray-800 text-base">Top Casas</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Ordenadas por ingresos generados</p>
                </div>
                <div className="w-8 h-8 rounded-xl bg-emerald-50 flex items-center justify-center">
                  <FaTrophy size={15} className="text-emerald-500" />
                </div>
              </div>
              {topCasas.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Sin datos aún</p>
              ) : (
                <div className="space-y-4">
                  {topCasas.map(({ casa, count, revenue }, idx) => (
                    <div key={casa.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                            idx === 0 ? "bg-amber-400 text-white" : idx === 1 ? "bg-gray-300 text-white" : idx === 2 ? "bg-orange-300 text-white" : "bg-gray-100 text-gray-500"
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-700 truncate max-w-[150px]">{casa.nombreDueno}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-emerald-600">${revenue.toFixed(0)}</span>
                          <span className="text-[10px] text-gray-400 ml-1">({count} svcs)</span>
                        </div>
                      </div>
                      <MiniBar value={revenue} max={maxCasaRevenue} color="bg-gradient-to-r from-emerald-400 to-teal-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Empleados */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="font-bold text-gray-800 text-base">Top Empleados</h3>
                  <p className="text-xs text-gray-400 mt-0.5">Por número de servicios realizados</p>
                </div>
                <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center">
                  <FaStar size={15} className="text-violet-500" />
                </div>
              </div>
              {topEmpleados.length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">Sin datos aún</p>
              ) : (
                <div className="space-y-4">
                  {topEmpleados.map(({ emp, count, horas }, idx) => (
                    <div key={emp.id}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center ${
                            idx === 0 ? "bg-amber-400 text-white" : idx === 1 ? "bg-gray-300 text-white" : idx === 2 ? "bg-orange-300 text-white" : "bg-gray-100 text-gray-500"
                          }`}>
                            {idx + 1}
                          </span>
                          <span className="text-sm font-semibold text-gray-700">{emp.nombre} {emp.apellidos}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-bold text-violet-600">{count} svcs</span>
                          <span className="text-[10px] text-gray-400 ml-1">({horas.toFixed(0)}h)</span>
                        </div>
                      </div>
                      <MiniBar value={count} max={maxEmpCount} color="bg-gradient-to-r from-violet-400 to-indigo-500" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── Alerts & Summary ─────────────────────────────────────────── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Alerts */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-gray-800 text-base mb-5 flex items-center gap-2">
                <FaExclamationTriangle className="text-amber-500" size={16} /> Alertas Activas
              </h3>
              <div className="space-y-3">
                {pagosUrgentes.length > 0 ? (
                  pagosUrgentes.map(emp => (
                    <div key={emp.id} className="flex items-center gap-3 p-3 bg-red-50 border border-red-100 rounded-xl">
                      <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-red-700">{emp.nombre} {emp.apellidos}</p>
                        <p className="text-xs text-red-500">Pago de quincena vencido</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                    <FaCheckCircle className="text-emerald-500" size={16} />
                    <p className="text-sm font-semibold text-emerald-700">Sin alertas pendientes</p>
                  </div>
                )}
                {cotizacionesPendientes > 0 && (
                  <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                    <div className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-amber-700">{cotizacionesPendientes} cotización(es) pendiente(s)</p>
                      <p className="text-xs text-amber-500">Revisar en la sección de Cotizaciones</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Mes vs prev */}
            <div className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm">
              <h3 className="font-bold text-gray-800 text-base mb-5 flex items-center gap-2">
                <FaChartBar className="text-blue-500" size={16} /> Comparativa Mensual
              </h3>
              <div className="space-y-4">
                {[
                  {
                    label: "Servicios realizados",
                    current: mesServicios.length,
                    prev: prevMesServicios.length,
                    format: (v: number) => String(v),
                    color: "text-blue-600",
                  },
                  {
                    label: "Horas trabajadas",
                    current: horasMes,
                    prev: horasPrevMes,
                    format: (v: number) => `${v.toFixed(1)}h`,
                    color: "text-violet-600",
                  },
                  {
                    label: "Ingresos",
                    current: gananciasMes,
                    prev: gananciasPrevMes,
                    format: (v: number) => `$${v.toFixed(0)}`,
                    color: "text-emerald-600",
                  },
                ].map((m, i) => {
                  const diff = m.prev > 0 ? ((m.current - m.prev) / m.prev) * 100 : 0;
                  const up = m.current >= m.prev;
                  return (
                    <div key={i} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0">
                      <div>
                        <p className="text-[15px] text-gray-600 font-medium">{m.label}</p>
                        <p className={`text-xl font-bold ${m.color}`}>{m.format(m.current)}</p>
                      </div>
                      <div className="text-right">
                        <div className={`flex items-center gap-1 justify-end text-sm font-semibold ${up ? "text-emerald-600" : "text-red-500"}`}>
                          {up ? <FaArrowUp size={11} /> : <FaArrowDown size={11} />}
                          {Math.abs(diff).toFixed(1)}%
                        </div>
                        <p className="text-xs text-gray-400">vs {MESES[prevMonth].slice(0,3)}: {m.format(m.prev)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
